/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  getTotalBudgetString,
  getPPPDString,
} from '@marageti/z4-lib';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { titleCase } from '../../../../utils/helpers';
import { formatCountryCodesToString } from '../../utils';
import { TravelerChips, TripMatchStatusChip } from '../../chips';

const {
  OutlinedUserGroupIcon,
  OutlinedLocationMarkerIcon,
  OutlinedCurrencyDollarIcon,
  OutlinedUserIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);

const showTotalTripPrice = (tripMatch: TripMatch) => [
  TripMatchStatus.MatchSold,
  TripMatchStatus.MatchSoldCanceled,
  TripMatchStatus.MatchRefundedCanceled,
].includes(tripMatch.status);

type AllCardViewProps = {
  countryCodes: CountryCodeMap | undefined;
  tripMatch: TripMatch;
};

const AllCardView = ({
  countryCodes,
  tripMatch,
}: AllCardViewProps) => {
  const {
    travelers,
    countries,
    departureDate: tmDepartureDate,
    totalNights: tmTotalNights,
    numberOfAdults,
    numberOfChildren,
    budget,
    partnerName,
    createdAt,
    pppd,
    totalTripPrice,
    totalPppd,
  } = tripMatch;
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const fullName = titleCase(travelers[0].fullName);
  const countryNames = countryCodes ? formatCountryCodesToString(countries, countryCodes) : '';
  const departureDate = getFormattedDateShortMonthDDYYYY(tmDepartureDate) || '';
  const totalNights = `${tmTotalNights} Nights`;
  const numberOfTravelers = numberOfAdults + numberOfChildren;
  const totalTravelers = numberOfTravelers === 1
    ? '1 Traveler'
    : `${numberOfTravelers} Travelers`;
  const matchedDate = getFormattedDateShortMonthDDYYYY(createdAt) || '';

  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={`/trip-matches/${tripMatch?.id}`}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography variant="bodyBold">
                {fullName}
              </Typography>
              <TravelerChips
                travelers={travelers}
                referralType={tripMatch.referralType}
                repeatTraveler={tripMatch.repeatTraveler}
              />
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedUserGroupIcon} label={totalTravelers} />
              <IconLabel icon={OutlinedLocationMarkerIcon} label={countryNames} />
              <IconLabel
                icon={DepartureIcon}
                label={(
                  <>
                    {departureDate}
                    {' '}
                    ·
                    {' '}
                    {totalNights}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedCurrencyDollarIcon}
                label={
                showTotalTripPrice(tripMatch) ? (
                  <>
                    {getTotalBudgetString(totalTripPrice.amount, 2, 2, totalTripPrice.currencyCode)}
                    {' '}
                    Total Trip Price ·
                    {' '}
                    {getPPPDString(totalPppd, totalTripPrice.currencyCode)}
                  </>
                ) : (
                  <>
                    {getTotalBudgetString(budget)}
                    {' '}
                    Total budget ·
                    {' '}
                    {getPPPDString(pppd)}
                  </>
                )
              }
              />
              <IconLabel icon={OutlinedUserIcon} label={partnerName} />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                <TripMatchStatusChip
                  status={tripMatch.status}
                />
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1}>
            <Grid item xs={2.5}>
              <Stack direction="row" spacing={1}>
                <Typography variant="bodyBold">
                  {fullName}
                </Typography>
                <TravelerChips
                  travelers={travelers}
                  referralType={tripMatch.referralType}
                  repeatTraveler={tripMatch.repeatTraveler}
                />
              </Stack>
              <Typography variant="caption">
                {countryNames}
              </Typography>
            </Grid>

            <Grid item xs={2.5}>
              <Typography variant="body">
                {departureDate}
              </Typography>
              <Typography variant="caption">
                {totalNights}
                {' '}
                ·
                {' '}
                {totalTravelers}
              </Typography>
            </Grid>

            <Grid item xs={2.5}>
              {showTotalTripPrice(tripMatch) ? (
                <>
                  <Typography variant="body">
                    {getPPPDString(totalPppd, totalTripPrice.currencyCode)}
                  </Typography>
                  <Typography variant="caption">
                    {getTotalBudgetString(totalTripPrice.amount, 2, 2, totalTripPrice.currencyCode)}
                    {' '}
                    Total Trip Price
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body">
                    {getPPPDString(pppd)}
                  </Typography>
                  <Typography variant="caption">
                    {getTotalBudgetString(budget)}
                    {' '}
                    Total Budget`

                  </Typography>

                </>
              )}
            </Grid>

            <Grid item xs={3}>
              <Typography variant="body">
                {partnerName}
              </Typography>
              <Typography variant="caption">
                Matched:
                {' '}
                {matchedDate}
              </Typography>
            </Grid>
            <Grid item xs={1.5} display="flex" justifyContent="flex-end">
              <TripMatchStatusChip
                status={tripMatch.status}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AllCardView;
